import { motion } from "framer-motion";
import { Lock, ShieldCheck, Flame, Sparkles, Users } from "lucide-react";
import { MiniCountdown } from "@/components/MiniCountdown";

/**
 * Editorial "exclusive to your school" WOW panel that sits directly beneath
 * the product name/price on the PDP. Its job is to translate the abstract
 * "dress lock" mechanic into a visceral status card that reads immediately.
 *
 * Renders one of five states:
 *   1. purchased          — permanently secured at YOUR school
 *   2. mine (holding)     — countdown + urgency
 *   3. locked-by-other    — bump CTA
 *   4. available (auth)   — "no one at YOUR school has locked it"
 *   5. available (public) — "one dress per WI school" pitch
 */
export default function ExclusivityPanel({
  product,
  lock,
  user,
  schoolName,
  interest,
  onLock,
  onBump,
  onSecureForever,
  onShareLook,
  onCallToLogin,
  busy = false,
}) {
  // Non-lockable products (tuxes, accessories, shoes) — omit
  if (product.lockable === false) return null;

  const mine = lock && user && lock.user_id === user.id;
  const lockedByOther = lock && lock.status === "active" && !mine;
  const purchased = lock && lock.status === "purchased";
  const school = schoolName || (user?.school_id ? "your school" : null);

  // -------------------- PURCHASED --------------------
  if (purchased) {
    return (
      <div
        data-testid="exclusivity-purchased"
        className="relative mt-6 overflow-hidden rounded-sm border border-rosemetal/50 bg-gradient-to-br from-rosemetal via-[#8f5560] to-[#5c3841] text-ivory shadow-[0_20px_50px_-15px_rgba(183,110,121,0.55)]"
      >
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #F7E7CE 1px, transparent 1px)", backgroundSize: "70px 70px" }} />
        <div className="relative p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-full bg-champagne/20 border border-champagne/40 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-champagne" strokeWidth={1.75} />
            </span>
            <p className="font-label text-[10px] text-champagne tracking-[0.32em]">◉ PERMANENTLY SECURED</p>
          </div>
          <h3 className="font-display text-3xl leading-tight">This dress is<br /><span className="italic text-champagne">off the market</span>{school && <> at {school}</>}.</h3>
          <p className="font-body text-sm text-ivory/75 mt-3 leading-relaxed">
            No one else at your school can wear it this season. Your night, your mirror moment.
          </p>
        </div>
      </div>
    );
  }

  // -------------------- MINE / HOLDING --------------------
  if (mine) {
    return (
      <div
        data-testid="exclusivity-mine"
        className="relative mt-6 overflow-hidden rounded-sm border border-champagne/50 bg-gradient-to-br from-ink via-[#1a0e0f] to-[#251519] text-ivory shadow-[0_20px_50px_-15px_rgba(230,196,143,0.35)]"
      >
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-rosemetal/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-16 -left-10 w-52 h-52 rounded-full bg-champagne/15 blur-3xl" aria-hidden />
        <div className="relative p-6">
          <div className="flex items-center gap-3 mb-3">
            <motion.span
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="w-10 h-10 rounded-full bg-champagne/15 border border-champagne/50 flex items-center justify-center"
            >
              <Flame className="w-5 h-5 text-champagne" strokeWidth={1.75} />
            </motion.span>
            <p className="font-label text-[10px] text-champagne tracking-[0.32em]">◉ HELD JUST FOR YOU</p>
          </div>
          <h3 className="font-display text-3xl leading-tight">Only <span className="italic text-champagne">you</span> can see this dress right now.</h3>
          <p className="font-body text-sm text-ivory/70 mt-3 leading-relaxed">
            When the timer runs out, she goes back in the catalog for everyone at your school. Secure her forever, or flex the lock 👇
          </p>
          <div className="mt-5 bg-ivory/8 border border-ivory/15 rounded-sm px-4 py-3 flex items-center justify-between">
            <p className="font-label text-[9px] text-champagne tracking-widest">TIME REMAINING</p>
            <div className="font-display italic text-2xl tabular-nums">
              <MiniCountdown expiresAt={lock.expires_at} />
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              onClick={onSecureForever}
              disabled={busy}
              data-testid="secure-forever-btn"
              className="flex-1 bg-champagne text-ink px-6 py-3.5 font-label text-[10px] tracking-[0.28em] rounded-sm hover:bg-ivory transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Lock className="w-3.5 h-3.5" strokeWidth={2} /> MAKE IT MINE · ${product.price}
            </button>
            <button
              onClick={onShareLook}
              className="flex-1 border border-champagne/60 text-champagne px-6 py-3.5 font-label text-[10px] tracking-[0.28em] rounded-sm hover:bg-champagne hover:text-ink transition-colors inline-flex items-center justify-center gap-2"
              data-testid="share-look-btn"
            >
              <Sparkles className="w-3.5 h-3.5" /> SHARE MY LOOK
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------- LOCKED BY ANOTHER STUDENT --------------------
  if (lockedByOther) {
    return (
      <div
        data-testid="exclusivity-locked-by-other"
        className="relative mt-6 overflow-hidden rounded-sm border border-rosemetal/40 bg-blush/70"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-full bg-rosemetal/15 border border-rosemetal/35 flex items-center justify-center">
              <Users className="w-5 h-5 text-rosemetal" strokeWidth={1.75} />
            </span>
            <p className="font-label text-[10px] text-rosemetal tracking-[0.32em]">◉ SHE'S HOLDING · YOU CAN JUMP THE LINE</p>
          </div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight text-ink">
            <b>{lock.user_name || "Another student"}</b> is holding this dress at your school.
          </h3>
          <p className="font-body text-sm text-ink/70 mt-3 leading-relaxed">
            Pay a $19 bump and the lock transfers to you the moment her timer expires.
          </p>
          <button
            onClick={onBump}
            disabled={busy}
            data-testid="bump-btn"
            className="mt-5 bg-rosemetal text-ivory px-6 py-3.5 font-label text-[10px] tracking-[0.28em] rounded-sm hover:bg-ink transition-colors inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Flame className="w-3.5 h-3.5" strokeWidth={2} /> PAY $19 TO BUMP
          </button>
        </div>
      </div>
    );
  }

  // -------------------- AVAILABLE — TWO VARIANTS --------------------
  // 5.a: authenticated student with a school — personalized copy
  // 5.b: public / logged-out — teaches the mechanic
  const authed = Boolean(user);
  const savedCount = interest?.saved || 0;
  const viewingCount = interest?.viewing || 0;

  return (
    <div
      data-testid="exclusivity-available"
      className="relative mt-6 overflow-hidden rounded-sm border border-ink/85 bg-ink text-ivory shadow-[0_20px_50px_-15px_rgba(10,10,10,0.45)]"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-champagne/12 blur-3xl" aria-hidden />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-rosemetal/15 blur-3xl" aria-hidden />
      <div className="relative p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-10 h-10 rounded-full bg-champagne/12 border border-champagne/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-champagne" strokeWidth={1.75} />
          </span>
          <p className="font-label text-[10px] text-champagne tracking-[0.32em]">◉ EXCLUSIVE TO ONE STUDENT PER SCHOOL</p>
        </div>
        <h3 className="font-display text-3xl leading-tight">
          {authed && school ? (
            <>No one at <span className="italic text-champagne">{school}</span> has locked this dress yet.</>
          ) : (
            <>One dress. <span className="italic text-champagne">One school.</span> One student.</>
          )}
        </h3>
        <p className="font-body text-sm text-ivory/70 mt-3 leading-relaxed">
          The moment you lock this look, it disappears from every other student's boutique at your school. Two-hour hold while you decide. Zero risk to try.
        </p>

        {/* Live urgency rail */}
        {(viewingCount > 0 || savedCount > 0) && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[10px] font-label tracking-widest">
            {viewingCount > 0 && (
              <span className="inline-flex items-center gap-1 bg-champagne/12 border border-champagne/25 text-champagne px-2.5 py-1 rounded-full">
                <Flame className="w-3 h-3" /> {viewingCount} EYEING NOW
              </span>
            )}
            {savedCount > 0 && (
              <span className="inline-flex items-center gap-1 bg-rosemetal/15 border border-rosemetal/30 text-rosemetal px-2.5 py-1 rounded-full">
                <Sparkles className="w-3 h-3" /> {savedCount} SAVED
              </span>
            )}
          </div>
        )}

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <button
            onClick={authed ? onLock : onCallToLogin}
            disabled={busy}
            data-testid="lock-btn"
            className="flex-1 bg-champagne text-ink px-6 py-3.5 font-label text-[10px] tracking-[0.3em] rounded-sm hover:bg-ivory transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Lock className="w-3.5 h-3.5" strokeWidth={2} /> LOCK · 2-HOUR HOLD
          </button>
        </div>
        <p className="mt-3 text-[10px] font-label tracking-widest text-ivory/45">
          NO PAYMENT · CANCEL ANYTIME WITHIN THE HOLD WINDOW
        </p>
      </div>
    </div>
  );
}

export { ExclusivityPanel };
